import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../../store/slices/authSlice';
import { getDefaultPath } from '../../helpers/roleUtils';
import { toast } from 'react-toastify';

const NEW_LOGIN_URL = 'https://admin-new.radiofx.co/login';
const CHECK_REDIRECT_URL = 'https://rfx-mission-control-670723456691.us-central1.run.app/api/check-redirect';
const LOGIN_HANDOFF_URL = 'https://rfx-mission-control-670723456691.us-central1.run.app/api/login-handoff';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [otherError, setOtherError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    username: false,
    password: false
  }); 

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && user) {
      const defaultPath = getDefaultPath(user);
      navigate(defaultPath);
    }
  }, [navigate, user]);

  useEffect(() => {
    const logoutMessage = sessionStorage.getItem('logoutMessage');
    if (logoutMessage) {
      setOtherError(logoutMessage);
      toast.error(logoutMessage, { position: 'top-right' });
      sessionStorage.removeItem('logoutMessage'); // Remove after showing once
    }
  }, [dispatch]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // Clear validation error when typing
    if (validationErrors[id]) {
      setValidationErrors(prev => ({
        ...prev,
        [id]: false
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const validateForm = () => {
    const errors = {
      username: !formData.username,
      password: !formData.password
    };
    setValidationErrors(errors);
    return !errors.username && !errors.password;
  };

  const handleLogin = async () => {
    setLoading(true);

    // Checked against a hosted allow-list (see rfx-mission-control) before
    // anything else. A match skips this system's login entirely — whatever
    // was typed goes straight to the new site for it to validate, this app
    // never checks the password itself. If the check itself fails (e.g. the
    // service is down), fail open and treat it as a normal login.
    let shouldRedirectToNewAdmin = false;
    try {
      const redirectCheck = await fetch(
        `${CHECK_REDIRECT_URL}?email=${encodeURIComponent(formData.username)}`
      );
      if (redirectCheck.ok) {
        const { redirect } = await redirectCheck.json();
        shouldRedirectToNewAdmin = Boolean(redirect);
      }
    } catch (error) {
      console.error('Redirect check failed, continuing with normal login:', error);
    }

    if (!validateForm()) {
      toast.error('Please fill in all fields', {
        position: 'top-right',
      });
      setLoading(false);
      return;
    }

    if (shouldRedirectToNewAdmin) {
      // Hand the typed credentials to the new admin site via a short-lived,
      // single-use code stored server-side — never a raw password in the
      // URL — carried across via a cookie scoped to the shared parent
      // domain, invisible to the user. The new site does its own login;
      // this app never validates the password.
      try {
        const handoffResponse = await fetch(LOGIN_HANDOFF_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: formData.username, password: formData.password }),
        });
        if (handoffResponse.ok) {
          const { code } = await handoffResponse.json();
          document.cookie = `sso_handoff=${code}; domain=.radiofx.co; path=/; max-age=60; secure; samesite=lax`;
        }
      } catch (error) {
        console.error('Failed to create login handoff, redirecting without it:', error);
      }
      window.location.replace(NEW_LOGIN_URL);
      return;
    }

    try {
      const response = await dispatch(loginUser(formData))
        .unwrap()

      if (response?.data?.id) {
        const defaultPath = getDefaultPath(response.data);
        navigate(defaultPath);
      }
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="loginBox">
      <div className="container d-flex justify-content-center align-items-center min-vh-100">
        <div className="col-md-6 col-lg-6">
          <div className="HeadingText text-center mb-4">
            <p className="h3">Admin Panel</p>
          </div>

          <div className="card">
            <div className="card-body px-4 py-5">
              <div className="cardText text-center mb-4">
                <p className="heading h4">Let's get started</p>
                <p className="smallHeading text-muted small">Please use Chrome or Firefox browser</p>
              </div>

              <div className="FormDiv">
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Email"
                    className={`form-control ${validationErrors.username ? 'is-invalid' : ''}`}
                    id="username"
                    value={formData.username}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                  />
                  {validationErrors.username && (
                    <div className="invalid-feedback">Please enter your email</div>
                  )}
                </div>

                <div className="mb-4">
                  <input
                    type="password"
                    placeholder="Password"
                    className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
                    id="password"
                    value={formData.password}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                  />
                  {validationErrors.password && (
                    <div className="invalid-feedback">Please enter your password</div>
                  )}
                  {otherError && (
                    <div className="invalid-feedback">{otherError}</div>
                  )}

                </div>

                <div className="btnDiv text-center">
                  <button
                    className="default-btn btn btn-primary w-100 py-2"
                    onClick={handleLogin}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        Please wait...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-5 text-center">
                <div>
                  <Link to="https://radiofxinc.com/" target='_blank' className="text-decoration-none">
                    I don't have an account
                  </Link>
                </div>
                <div className="mt-3">
                  <Link to="/auth/restore-password" className="text-decoration-none text-muted">
                    Restore Password
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;